import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ReactComponent as BackrsFilledDropShadow } from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { BackrsForm } from '@backrs_ui/common-ui/src/molecules/BackrsForm/BackrsForm';
import {
  BackrsFormField,
  BackrsFormValues,
} from '@backrs_ui/common-ui/src/molecules/BackrsForm/types';

import { Box, Typography, Theme, SxProps, ButtonBase } from '@mui/material';
import { useForm } from 'react-hook-form';

import { LoginCarousel } from './LoginCarousel';
import { ForgotPasswordPopup } from '../BackrsForm/ForgotPasswordPopup';

const LOGIN_STRINGS = appStrings.modules.login;

type LoginPageProps = Readonly<{
  title: string;
  handleLoggingIn: (phone: string, email: string, password: string) => void;
  handleSigningUp?: () => void;
  errorMessage?: string;
  customAction?: {
    text: string;
    onClick: () => void;
  };
  environmentUrl: string;
}>;

const defaultLoginValues: BackrsFormValues = {
  phone: '',
  email: '',
  password: '',
};

export function LoginPage({
  title,
  handleLoggingIn,
  handleSigningUp,
  errorMessage,
  environmentUrl,
}: LoginPageProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  const methods = useForm<BackrsFormValues>({
    defaultValues: defaultLoginValues,
    mode: 'onSubmit',

    resolver: async (values) => {
      const errors: Record<string, { type: string; message: string }> = {};

      if (!values.phone && !values.email) {
        errors.phone = {
          type: 'manual',
          message: LOGIN_STRINGS.error.phoneEmailRequired,
        };
      }

      if (values.phone && !/^\d{10}$/.test(values.phone)) {
        errors.phone = {
          type: 'pattern',
          message: LOGIN_STRINGS.error.invalidPhone,
        };
      }

      if (values.email && !/^\S+@\S+$/.test(values.email)) {
        errors.email = {
          type: 'pattern',
          message: LOGIN_STRINGS.error.invalidEmail,
        };
      }

      if (values.email && !values.password && !values.phone) {
        errors.password = {
          type: 'required',
          message: LOGIN_STRINGS.error.passwordRequired,
        };
      }

      return {
        values,
        errors,
      };
    },
  });
  const phoneValue = methods.watch('phone');
  const emailValue = methods.watch('email');
  const passwordValue = methods.watch('password');

  const fields: BackrsFormField[] = [
    {
      id: 'email',
      name: 'email',
      placeholder: LOGIN_STRINGS.emailPlaceholder,
      type: 'text',
      autoComplete: 'email',
      rules: {
        required: !phoneValue ? LOGIN_STRINGS.error.emailRequired : false,
        pattern: {
          value: /^\S+@\S+\.\S+$/,
          message: LOGIN_STRINGS.error.invalidEmail,
        },
      },
      disabled: !!phoneValue,
      autoFocus: true,
    },
    {
      id: 'password',
      name: 'password',
      placeholder: LOGIN_STRINGS.passwordPlaceholder,
      type: isPasswordVisible ? 'text' : 'password',
      autoComplete: 'current-password',
      rules: {
        required: !phoneValue ? LOGIN_STRINGS.error.passwordRequired : false,
      },
      icon: {
        type: 'RIGHT_ICON',
        rightIcon: isPasswordVisible ? 'EyeOff' : 'Eye',
        onRightIconClick: () => {
          setIsPasswordVisible((prev) => !prev);

          setTimeout(() => {
            setIsPasswordVisible(false);
          }, 5000);
        },
      },
      disabled: !!phoneValue,
      divider: true,
      dividerText: LOGIN_STRINGS.dividerText,
    },
    {
      id: 'phone',
      name: 'phone',
      placeholder: LOGIN_STRINGS.phonePlaceholder,
      type: 'tel',
      autoComplete: 'phone',
      rules: {
        pattern: {
          value: /^\d{10}$/,
          message: LOGIN_STRINGS.error.invalidPhone,
        },
      },
      disabled: !!emailValue && !!passwordValue,
    },
  ];

  const handleSubmission = (data: BackrsFormValues) => {
    handleLoggingIn(data.phone, data.email, data.password);
  };

  return (
    <Box sx={componentContainerSx}>
      <Box sx={containerStyle}>
        <Box sx={loginFormStyle}>
          <Box sx={responsiveBackrsLogoStyle}>
            <BackrsFilledDropShadow />
          </Box>
          <Box sx={textContainerSx}>
            <Typography
              variant="display-xs-semibold"
              color={(theme) => theme.backrs.colors['Colors/Text/text-primary']}
            >
              {title}
            </Typography>
            <Typography
              variant="text-md-regular"
              color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
              sx={{ textAlign: 'center' }}
            >
              {LOGIN_STRINGS.welcomeBack}
            </Typography>
          </Box>
          <BackrsForm
            fields={fields}
            methods={methods}
            onSubmit={handleSubmission}
            buttonLabel={appStrings.buttonText.signIn}
          >
            <ButtonBase
              sx={forgotPasswordContainerStyle}
              onClick={() => setIsForgotPasswordOpen(true)}
              disableRipple
            >
              <Typography variant="text-sm-semibold" sx={forgotPasswordStyle}>
                {LOGIN_STRINGS.forgotPassword}
              </Typography>
            </ButtonBase>
          </BackrsForm>
          <ForgotPasswordPopup
            isOpen={isForgotPasswordOpen}
            setIsOpen={setIsForgotPasswordOpen}
            environmentUrl={environmentUrl}
          />
          {errorMessage && <Box sx={errorMessageSx}>{errorMessage}</Box>}
          {handleSigningUp && (
            <Box sx={handleSignUpSx}>
              <Typography component="span" variant="text-sm-regular">
                {LOGIN_STRINGS.dontHaveAccount}
              </Typography>{' '}
              <Typography
                component="span"
                variant="text-sm-regular"
                sx={embeddedButtonSx}
                onClick={handleSigningUp}
              >
                {LOGIN_STRINGS.signUp}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={carouselContainerSx}>
        <LoginCarousel />
      </Box>
    </Box>
  );
}

const errorMessageSx: SxProps<Theme> = ({ backrs }) => ({
  mt: 2,
  color: backrs.colors['Colors/Text/text-error-primary'],
  '@media (min-width: 900px) and (max-height: 620px)': {
    mt: '-2%',
    mb: '-5%',
  },
});

const handleSignUpSx: SxProps<Theme> = ({ backrs }) => ({
  paddingBottom: backrs.spacing['spacing-xl'],
});

const embeddedButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline',
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
  cursor: 'pointer',
});

const componentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: '1fr 1fr',
  },
  color: backrs.colors['Yellow/950'],
  height: '100dvh',
  overflow: 'hidden',
});

const carouselContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: {
    xs: 'none',
    md: 'block',
  },
  backgroundColor: backrs.colors['Warning/400'],
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  width: '100%',
  height: '100dvh',
  borderLeft: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  margin: '0 auto',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: {
    xs: 'flex-start',
    md: 'center',
  },
  alignSelf: 'center',
  justifySelf: 'center',
  px: {
    xs: backrs.spacing['spacing-lg'],
    sm: backrs.spacing['spacing-2xl'],
  },
  backgroundColor: backrs.colors['Beige/50'],
  height: '100dvh',
  width: '100%',
  py: {
    xs: backrs.spacing['spacing-6xl'],
    md: backrs.spacing['spacing-4xl'],
  },
});

const loginFormStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: {
    xs: backrs.colors['Beige/50'],
    sm: backrs.colors['Colors/Base/White'],
  },
  paddingX: {
    xs: backrs.spacing['spacing-md'],
    sm: backrs.spacing['spacing-2xl'],
  },
  borderRadius: {
    xs: 0,
    sm: backrs.radius['radius-md'],
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: {
    xs: '100%',
    sm: '440px',
  },
  width: '100%',
  boxSizing: 'border-box',
  '@media (min-width: 300px)': {
    paddingX: backrs.spacing['spacing-xl'],
  },
  '@media (min-width: 600px)': {
    paddingX: backrs.spacing['spacing-4xl'],
  },
  boxShadow: {
    xs: 'none',
    sm: backrs.shadows['shadow-3xl'],
  },
  border: {
    xs: 'none',
    sm: `3px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  margin: {
    xs: 0,
    sm: `auto ${backrs.spacing['spacing-4xl']}`,
    md: `auto ${backrs.spacing['spacing-4xl']}`,
    lg: `auto ${backrs.spacing['spacing-6xl']}`,
  },
  gap: {
    xs: backrs.spacing['spacing-3xl'],
    md: backrs.spacing['spacing-xl'],
  },
  pb: {
    xs: backrs.spacing['spacing-3xl'],
    md: backrs.spacing['spacing-4xl'],
  },
});

const responsiveBackrsLogoStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: {
    xs: '50px',
    md: '60px',
  },
  maxHeight: {
    xs: '50px',
    md: '60px',
  },
  mt: backrs.spacing['spacing-3xl'],
});

const forgotPasswordContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'block',
  ml: 'auto',
  mr: backrs.spacing['spacing-md'],
});

const forgotPasswordStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/500'],
});

const textContainerSx: SxProps<Theme> = (theme: Theme) => ({
  ...loginFormStyle(theme),
  pb: 0,
  gap: '.5rem',
  outline: 'none',
  border: 'none',
  boxShadow: 'none',
});
